"use client";

import { c as _c } from "react/compiler-runtime";
import type { Key, KeyboardEvent, ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { TableSkeletonRows } from "@/shared/ui/skeletons";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import type { ListTableColumn } from "./types";
const ALIGN_CLASS = {
  left: "",
  center: "text-center",
  right: "text-right"
} as const;
const DEFAULT_WRAPPER = "overflow-x-auto rounded-lg border";
function handleRowActivation<TData>(event: KeyboardEvent<HTMLTableRowElement>, row: TData, onRowClick: (row: TData) => void) {
  if (event.target !== event.currentTarget) return;
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  onRowClick(row);
}
export interface ListTableProps<TData> {
  columns: ListTableColumn<TData>[];
  /** Row data — auto-renders body via `columns[].cell`. Ignored when `children` is provided. */
  data?: TData[];
  getRowId?: (row: TData, index: number) => Key;
  onRowClick?: (row: TData) => void;
  canClickRow?: (row: TData, index: number) => boolean;
  rowClassName?: (row: TData) => string | undefined;
  rowDataTestId?: (row: TData) => string | undefined;
  loading?: boolean;
  skeletonColumnCount?: number;
  skeletonRowCount?: number;
  /** Content rendered inside `<TableFooter>`. */
  footer?: ReactNode;
  /**
   * Wrapper `<div>` around `<Table>`. Defaults to `overflow-x-auto rounded-lg border`.
   * Pass `false` when the parent already provides a border/card surface.
   */
  wrapperClassName?: string | false;
  /** className on the header `<TableRow>`. */
  headerRowClassName?: string;
  /** className on the `<Table>` element. */
  tableClassName?: string;
  /** When provided, overrides automatic body rendering. Useful for rows that need per-row local state. */
  children?: ReactNode;
}
export function ListTable(t0) {
  const $ = _c(29);
  const {
    columns,
    data,
    getRowId,
    onRowClick,
    canClickRow,
    rowClassName,
    rowDataTestId,
    loading: t1,
    skeletonColumnCount,
    skeletonRowCount,
    footer,
    wrapperClassName,
    headerRowClassName,
    tableClassName,
    children
  } = t0;
  const loading = t1 === undefined ? false : t1;
  const showSkeleton = !children && loading && (!data || data.length === 0);
  let t2;
  if ($[0] !== columns) {
    t2 = columns.map(_temp);
    $[0] = columns;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== headerRowClassName || $[3] !== t2) {
    t3 = <TableHeader><TableRow className={headerRowClassName}>{t2}</TableRow></TableHeader>;
    $[2] = headerRowClassName;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== canClickRow || $[6] !== children || $[7] !== columns || $[8] !== data || $[9] !== getRowId || $[10] !== onRowClick || $[11] !== rowClassName || $[12] !== rowDataTestId || $[13] !== showSkeleton || $[14] !== skeletonColumnCount || $[15] !== skeletonRowCount) {
    t4 = showSkeleton ? <TableSkeletonRows columnCount={skeletonColumnCount ?? columns.length} rowCount={skeletonRowCount} /> : children ?? data?.map((row, index) => {
      const key = getRowId ? getRowId(row, index) : index;
      const extraClassName = rowClassName?.(row);
      const testId = rowDataTestId?.(row);
      const rowClick = onRowClick && (canClickRow?.(row, index) ?? true) ? onRowClick : undefined;
      const isClickable = Boolean(rowClick);
      return <TableRow key={key} className={cn(isClickable ? "cursor-pointer" : undefined, extraClassName)} onClick={rowClick ? () => rowClick(row) : undefined} onKeyDown={rowClick ? event => handleRowActivation(event, row, rowClick) : undefined} tabIndex={isClickable ? 0 : undefined} data-testid={testId}>{columns.map(col_0 => <TableCell key={col_0.id} className={cn(col_0.align ? ALIGN_CLASS[col_0.align] : undefined, col_0.cellClassName)}>{col_0.cell ? col_0.cell(row, index) : null}</TableCell>)}</TableRow>;
    });
    $[5] = canClickRow;
    $[6] = children;
    $[7] = columns;
    $[8] = data;
    $[9] = getRowId;
    $[10] = onRowClick;
    $[11] = rowClassName;
    $[12] = rowDataTestId;
    $[13] = showSkeleton;
    $[14] = skeletonColumnCount;
    $[15] = skeletonRowCount;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t4) {
    t5 = <TableBody>{t4}</TableBody>;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== footer) {
    t6 = footer ? <TableFooter>{footer}</TableFooter> : null;
    $[19] = footer;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t3 || $[22] !== t5 || $[23] !== t6 || $[24] !== tableClassName) {
    t7 = <Table className={tableClassName}>{t3}{t5}{t6}</Table>;
    $[21] = t3;
    $[22] = t5;
    $[23] = t6;
    $[24] = tableClassName;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const table = t7;
  if (wrapperClassName === false) {
    return table;
  }
  const t8 = wrapperClassName ?? DEFAULT_WRAPPER;
  let t9;
  if ($[26] !== t8 || $[27] !== table) {
    t9 = <div className={t8}>{table}</div>;
    $[26] = t8;
    $[27] = table;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function _temp(col) {
  return <TableHead key={col.id} aria-sort={col.ariaSort} className={cn(col.align ? ALIGN_CLASS[col.align] : undefined, col.headerClassName)}>{col.header}</TableHead>;
}